import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import './tabs.css';

export interface AppTabsProps {
  /** Tab elements — typically `<AppTab>` children. */
  children: ReactNode;
  /** Platform variant — "mobile" tightens padding for PWA. */
  variant?: 'desktop' | 'mobile';
  /** Additional class names appended to `.app-tabs`. */
  className?: string;
  /** Optional accessible label for the tab list (screen readers). */
  'aria-label'?: string;
}

/**
 * Per-app second-level tab bar (FR-021, FR-026).
 *
 * Renders a horizontally-scrollable nav below the platform shell. Per-app
 * typography is handled entirely via `tabs.css` descending from the
 * `body.theme-{app}` class set by {@link ThemeBodyClass}. Active state is
 * derived from the URL by the child `<AppTab>` elements (via `NavLink`), so
 * browser back/forward and deep links both work without app-level state.
 * The nav hides its scrollbar, so on each navigation the active tab is
 * scrolled into view — otherwise it can sit off-screen on a phone (#109).
 */
export function AppTabs({
  children,
  variant = 'desktop',
  className,
  'aria-label': ariaLabel = 'App sections',
}: AppTabsProps): JSX.Element {
  const navRef = useRef<HTMLElement>(null);
  const { pathname, search } = useLocation();
  const merged = ['app-tabs', className].filter(Boolean).join(' ');

  // `.app-tab--active` also covers prefix matches such as Beach on a
  // `/surftime/beach/<slug>` detail route, where `aria-selected` is false.
  useEffect(() => {
    navRef.current
      ?.querySelector('[aria-selected="true"], .app-tab--active')
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [pathname, search]);

  return (
    <nav
      ref={navRef}
      className={merged}
      data-variant={variant}
      role="tablist"
      aria-label={ariaLabel}
    >
      {children}
    </nav>
  );
}

export default AppTabs;
